import { BaseService } from "../../core/baseService";
import {
  OrderServiceInterface,
  GetOrdersParams,
  UpdateOrderStatusParams,
  FoodOrderMetricsDto,
  BulkUpdateOrderStatusParams,
  BulkUpdateOrderStatusResponse,
} from "../types/order.types";
import { AdminOrderDto } from "../../../models/AdminOrderDto";
import {
  DetailedOrderDto,
  BulkExportRequest,
  BulkExportResult,
} from "../../../models/DetailedOrderDto";
import { PaginatedResponse } from "../../../models";
import { ApiResult, CountResponse, extractCount } from "../../core/types";

export class OrderService extends BaseService implements OrderServiceInterface {
  async getOrders(
    params: GetOrdersParams,
  ): Promise<ApiResult<PaginatedResponse<AdminOrderDto>>> {
    const queryParams = this.buildQueryParams({
      pageNumber: params.page, // Backend expects 'pageNumber', not 'page'
      pageSize: params.pageSize,
      status: params.status,
      roomNumber: params.roomNumber,
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
      includeClosed: params.includeClosed,
    });

    return this.handleRequest<PaginatedResponse<AdminOrderDto>>(
      this.client.get("/api/admin/food-orders", { params: queryParams }),
    );
  }

  async updateOrderStatus(
    params: UpdateOrderStatusParams,
  ): Promise<ApiResult<AdminOrderDto>> {
    const { orderId, status } = params;

    return this.handleRequest<AdminOrderDto>(
      this.client.put(`/api/admin/food-orders/${orderId}/status`, {
        NewStatus: status,
      }),
    );
  }

  async getPendingCount(): Promise<ApiResult<number>> {
    const result = await this.handleRequest<CountResponse>(
      this.client.get("/api/admin/food-orders/pending-count"),
    );
    return extractCount(result);
  }

  async markOrderAsDelivered(
    orderId: string,
  ): Promise<ApiResult<AdminOrderDto>> {
    return this.handleRequest<AdminOrderDto>(
      this.client.put(`/api/admin/food-orders/${orderId}/deliver`),
    );
  }

  async cancelOrder(orderId: string): Promise<ApiResult<AdminOrderDto>> {
    return this.handleRequest<AdminOrderDto>(
      this.client.put(`/api/admin/food-orders/${orderId}/cancel`),
    );
  }

  async getOrderDetails(orderId: string): Promise<ApiResult<DetailedOrderDto>> {
    return this.handleRequest<DetailedOrderDto>(
      this.client.get(`/api/admin/food-orders/${orderId}/details`),
    );
  }

  async downloadOrderReceipt(
    orderId: string,
    language?: string,
  ): Promise<ApiResult<Blob>> {
    // Pass preferred language so backend can localize the PDF if supported
    return this.handleRequest<Blob>(
      this.client.get(`/api/admin/food-orders/${orderId}/receipt/pdf`, {
        responseType: "blob",
        headers: {
          "Accept-Language":
            language ||
            ((typeof navigator !== "undefined" && navigator.language) ||
              "en-US"),
        },
        params: language ? { lang: language } : undefined,
      }),
    );
  }

  async bulkExportOrders(
    request: BulkExportRequest,
  ): Promise<ApiResult<BulkExportResult>> {
    return this.handleRequest<BulkExportResult>(
      this.client.post("/api/admin/food-orders/export/bulk", request),
    );
  }

  async getExportStatus(jobId: string): Promise<ApiResult<BulkExportResult>> {
    return this.handleRequest<BulkExportResult>(
      this.client.get(`/api/admin/food-orders/export/status/${jobId}`),
    );
  }

  async downloadExport(jobId: string): Promise<ApiResult<Blob>> {
    return this.handleRequest<Blob>(
      this.client.get(`/api/admin/food-orders/export/download/${jobId}`, {
        responseType: "blob",
      }),
    );
  }

  async bulkUpdateOrderStatus(
    params: BulkUpdateOrderStatusParams,
  ): Promise<ApiResult<BulkUpdateOrderStatusResponse>> {
    return this.handleRequest<BulkUpdateOrderStatusResponse>(
      this.client.put("/api/admin/food-orders/bulk-status", {
        orderIds: params.orderIds,
        newStatus: params.newStatus,
      }),
    );
  }

  async getOrderMetrics(
    days: number = 7,
  ): Promise<ApiResult<FoodOrderMetricsDto>> {
    return this.handleRequest<FoodOrderMetricsDto>(
      this.client.get("/api/admin/food-orders/metrics", { params: { days } }),
    );
  }
}
